import React from 'react';
import {
  WizardMetaData,
  WizardPendoData,
  wizardStyles,
} from '../Wizard/Wizard';
import OutcomePopoverText from './OutcomePopoverText';
import ResultDataPopoverText from './ResultDataPopoverText';
import OutcomesList from './OutcomesList/OutcomesList';
import OutcomeDetails from './OutcomeDetails/OutcomeDetails';
import ConsolidatedMeasures from './ConsolidatedMeasures/ConsolidatedMeasures';
import OutcomesImage from 'assets/images/WizardOutcomes.png';
import OutcomeDetailsImage from 'assets/images/WizardOutcomeDetails.png';
import OutcomeResultsDataImage from 'assets/images/WizardResultsData.png';
import { Banner, Text } from 'components/core';
import { Stack } from 'components/layout';

const OutcomesDescription = () => {
  return (
    <>
      <OutcomePopoverText />
      <Banner className={wizardStyles.CustomBanner}>
        <Text>
          Think about your high-level outcomes that are important when
          evaluating the overall impact and effectiveness of an intervention,
          exposure or phenomenon of interest (e.g. Depression scores, Quality of
          life, Adverse events)
        </Text>
      </Banner>
    </>
  );
};

const OutcomeDetailsDescription = () => {
  return (
    <>
      <Text>
        For each outcome reported in the study, what outcome details do you want
        to collect?
      </Text>
      <Banner className={wizardStyles.CustomBanner}>
        <Text>
          Think about outcome details you need to collect to understand if
          outcomes across studies can be compared together (e.g. different
          scales, different units of measurement)
        </Text>
      </Banner>
    </>
  );
};

const OutcomesExampleDescription = () => {
  return (
    <Stack spacing={4}>
      <Text>
        1. You&apos;ll be able to select which outcomes were reported in the
        study.
      </Text>
      <Text>
        2. You can also create multiple sub-outcomes associated with each
        outcome. This is useful when there are multiple outcomes reported within
        different scales or different units of measurement.
      </Text>
    </Stack>
  );
};

const OutcomeDetailsExampleDescription = () => {
  return (
    <Stack spacing={4}>
      <Text>
        1. A table will be created with a row for each outcome detail and a
        column for each outcome reported in the study.
      </Text>
      <Text>
        2. Collecting this data will allow you to compare the differences
        between outcomes.
      </Text>
    </Stack>
  );
};

const OutcomeResultsDataExampleDescription = () => {
  return (
    <Stack spacing={4}>
      <Text>
        1. For each outcome reported in the study, a results data table will be
        created.
      </Text>
      <Text>
        2. Each group and timepoint reported in the study will have a new row,
        data types are the columns.
      </Text>
      <Text>
        3. You can change the data types that are shown for each study.
      </Text>
    </Stack>
  );
};

export const outcomesWizardConfig = (
  hasSelectableMeasures: boolean
): WizardMetaData => {
  const data: WizardMetaData = {
    1: {
      left: {
        heading: '1. Add outcomes',
        description: <OutcomesDescription />,
        fields: <OutcomesList fullWidth />,
        disableNavigationOnErrors: ['outcomes'],
      },
      right: {
        description: <OutcomesExampleDescription />,
        image: OutcomesImage,
      },
    },
    2: {
      left: {
        heading: '2. Select outcome details',
        description: <OutcomeDetailsDescription />,
        fields: <OutcomeDetails fullWidth />,
        disableNavigationOnErrors: ['outcomes'],
      },
      right: {
        description: <OutcomeDetailsExampleDescription />,
        image: OutcomeDetailsImage,
      },
    },
  };

  if (hasSelectableMeasures) {
    data[3] = {
      left: {
        heading: '3. Add result data formats',
        optional: true,
        description: <ResultDataPopoverText />,
        fields: <ConsolidatedMeasures fullWidth />,
        disableNavigationOnErrors: ['consolidated_measures'],
      },
      right: {
        description: <OutcomeResultsDataExampleDescription />,
        image: OutcomeResultsDataImage,
      },
    };
  }

  return data;
};

export const outcomesPendoKeys = {
  skip: 'outcomes-wizard-skip',
  next: 'outcomes-wizard-next',
  back: 'outcomes-wizard-back',
  done: 'outcomes-wizard-done',
  setupNow: 'outcomes-wizard-setup-now',
  editSetup: 'outcomes-wizard-edit-setup',
} as WizardPendoData;
